<script>
import con from '@services/connection'
import { cadastroAtivo } from '@constants/URL'
import * as CalculosConsolidador from '@utils/consolidador/CalculosConsolidador'
import { GlobalBus } from '@services/bus'
import {
  MODAL,
  BLACK_LIST_CALCULO_RENTABILIDADE,
  BLACK_LIST_DADOS_FALTANTE,
  RELOAD,
} from '@constants/Text'
import { toMoney, toPercent } from '@utils/Format'
import {
  allocationTotal,
  allocationPercent,
} from '@modules/consolidator/utils/allocation-percent/allocation-percent'
import {
  formatRentabilityByCurrency,
  isOffshoreInvestmentType,
} from '@modules/consolidator/utils/InvestmentType/InvestmentType.js'
import { buildSuitabilityMap } from '@modules/consolidator/utils/suitability-helpers'
import {
  computeTotalInvested,
  buildViewByClass,
  buildAllAssetIds,
  buildFlatAssets,
  buildClassStatsFmt,
  buildAssetStatsFmt,
  computeClasses,
  buildMapClassRent,
} from '@modules/consolidator/utils/FormatAssetsView/FormatAssetsView'
import { resolvePlTotal } from '@modules/consolidator/utils/resolve-pl-total/resolve-pl-total'
import AssetsByGroupTableHeader from '@modules/consolidator/components/assets-by-group-table/assets-by-group-table-header.vue'
import AssetsByGroupTableGroupRow from '@modules/consolidator/components/assets-by-group-table/assets-by-group-table-group-row.vue'
import AssetsByGroupTableRow from '@modules/consolidator/components/assets-by-group-table/assets-by-group-table-row.vue'
import {
  isColumnVisible,
  shouldShowActions,
} from '@modules/consolidator/utils/column-visibility/column-visibility'
import { WALLET_ASSET_COLUMN_CONFIG } from '@modules/consolidator/constants/wallet-asset-columns'

export default {
  name: 'AssetsByGroupTable',
  components: { AssetsByGroupTableHeader, AssetsByGroupTableGroupRow, AssetsByGroupTableRow },
  props: {
    // Vem da VIEW: ela conhece o cliente no created e dispara a fila em paralelo com as pesadas.
    sugestoesPendentesPorAtivo: { type: Object, default: null },
    // Contagem por MOVIMENTACAO, repassada ao modal de detalhes para o selo acender na aba de
    // movimentacoes. Antes o `handleOpenDetalhes` lia `sugestoesPorMovimentacaoDoCliente`, que nao
    // existe em lugar nenhum: chegava `undefined` e o selo nunca acendia por este caminho.
    sugestoesPorMovimentacao: { type: Object, default: null },
    movimentacoesDasSugestoes: { type: Object, default: null },
    statisticsAssets: { type: Object, default: () => ({}) },
    clienteAtual: { type: Object, default: () => ({}) },
    ativosSelecionados: { type: Array, default: () => [] },
    ativosSelecionadosPorClasse: { type: Object, default: () => ({}) },
    carteiraTabela: { type: Object, default: () => ({}) },
    estatisticasClasse: { type: Object, default: () => ({}) },
    composicaoFundos: { type: Object, default: () => ({}) },
    movimentacoes: { type: Object, default: () => ({}) },
    alertMarcacaoMercadoRF: { type: Object, default: () => ({}) },
    mapperBenchmarks: { type: Object, default: () => ({}) },
    filtroCorretora: { type: Array, default: () => [] },
    modoFiltroCorretora: { type: String, default: '' },
    loadingRentAsset: { type: Boolean, default: false },
    benchmarks: { type: Array, default: () => [] },
    searchQuery: { type: String, default: '' },
    paginationThreshold: { type: Number, default: 100 },
    defaultPageSize: {
      type: Number,
      default: 100,
      validator: (value) => value > 0,
    },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 25, 50, 100, 500, 'Todos'],
    },
    currency: { type: String, default: 'real' },
    investmentType: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    // Catalogo de colunas INJETAVEL: default = o da carteira do cliente, entao o
    // consumidor que nao passa nada nao muda em nada. A carteira do grupo passa
    // o seu, identico mais a coluna `membro`.
    columnsConfig: { type: Array, default: () => WALLET_ASSET_COLUMN_CONFIG },
    visibleColumns: { type: Array, default: () => [] },
    // Allowlist de acoes por CHAVE (`detalhes`, `editar`, ...). `null` = default,
    // o conjunto inteiro gateado por `readonly`. Array = exatamente essas.
    allowedActions: { type: Array, default: null },
    carteirasRecomendadas: { type: Array, default: () => [] },
    mostrarClasseAnbima: { type: Boolean, default: false },
    plTotal: { type: Number, default: null },
  },
  data() {
    return {
      viewByClass: {},
      classVolFmtByName: {},
      classInicioFmtByName: {},
      classRendFmtByName: {},
      assetStatsFmtById: {},
      allAssetIdsCache: [],
      displayClasses: [],
      groupPageMap: {},
      pageSizeByGroup: {},
      pageSize: this.defaultPageSize,
      totalInvested: 0,
    }
  },
  computed: {
    visibleClasses() {
      return this.buildVisibleClasses()
    },
    groups() {
      const list = this.visibleClasses || []
      if (!Array.isArray(list) || list.length === 0) return []
      const nodes = []
      for (let i = 0; i < list.length; i++) {
        const node = this.buildGroupNode({ classe: list[i] })
        if (node) nodes.push(node)
      }
      return nodes
    },
    allAssetIds() {
      return this.allAssetIdsCache
    },
    isAllSelected() {
      return (
        this.allAssetIds.length > 0 &&
        this.ativosSelecionadosInside.length === this.allAssetIds.length
      )
    },
    isSomeSelected() {
      const len = this.ativosSelecionadosInside.length
      return len > 0 && len < this.allAssetIds.length
    },
    ativosSelecionadosInside: {
      get() {
        return this.ativosSelecionados || []
      },
      set(newVal) {
        this.$emit('updateAtivosSelecionados', newVal)
      },
    },
    mapClassRent() {
      return buildMapClassRent({
        estatisticasClasse: this.estatisticasClasse,
        mapperBenchmarks: this.mapperBenchmarks,
        benchmarks: this.benchmarks,
      })
    },
    classes() {
      return computeClasses({
        carteiraTabela: this.carteiraTabela,
        viewByClass: this.viewByClass,
      })
    },
    benchmarksByClass() {
      const stats = this.estatisticasClasse || {}
      const bms = this.benchmarks || []
      const classKeys = Object.keys(stats).filter((k) => k !== 'Todos')
      const map = {}
      for (let i = 0; i < classKeys.length; i++) {
        const key = classKeys[i]
        const arr = []
        for (let j = 0; j < bms.length; j++) {
          const b = bms[j]
          const text = this.getClassRent(key, b)
          if (text !== '-') arr.push({ benchmark: b, text })
        }
        map[key] = arr
      }
      return map
    },
    classRendTextByName() {
      const statsByClass = this.estatisticasClasse || {}
      const fallbackByClass = this.classRendFmtByName || {}
      const table = this.carteiraTabela || {}
      const map = {}
      for (const className of Object.keys(table)) {
        const classStats = statsByClass[className] || {}
        map[className] = formatRentabilityByCurrency({
          rawValue: classStats.rendimento,
          currencyKey: 'real',
          fallbackValue: fallbackByClass[className] || '',
        })
      }
      return map
    },
    isPaginationEnabled() {
      return (this.allAssetIds || []).length > this.paginationThreshold
    },
    assetFlagsById() {
      return this.buildAssetFlagsById()
    },
    assetViewById() {
      return this.buildAssetViewById()
    },
    assetAplicacaoMaps() {
      const table = this.carteiraTabela || {}
      const textById = {}
      const negativeById = {}
      for (const className of Object.keys(table)) {
        const assets = (table[className] && table[className].carteira) || []
        for (const rawAsset of assets) {
          const a = rawAsset || {}
          const aplicacao = this.resolveAplicacao(a)
          textById[a.id] = aplicacao.text
          negativeById[a.id] = aplicacao.number < 0
        }
      }
      return { textById, negativeById }
    },
    assetAplicacaoFmtById() {
      return this.assetAplicacaoMaps.textById
    },
    assetNegativeById() {
      return this.assetAplicacaoMaps.negativeById
    },
    assetRendTextById() {
      const table = this.carteiraTabela || {}
      const map = {}
      for (const className of Object.keys(table)) {
        const assets = (table[className] && table[className].carteira) || []
        for (const rawAsset of assets) {
          const asset = rawAsset || {}
          map[asset.id] = this.resolveAssetRendText(asset)
        }
      }
      return map
    },
    hasBrokerFilter() {
      return (
        Array.isArray(this.filtroCorretora) && this.filtroCorretora.length > 0
      )
    },
    plTotalNumber() {
      return resolvePlTotal({
        backendTotal: this.plTotal,
        localTotal: this.totalInvested,
        hasBrokerFilter: this.hasBrokerFilter,
      })
    },
    plTotalText() {
      return toMoney(this.plTotalNumber, 2, this.currency)
    },
    plTotalNegative() {
      return this.plTotalNumber < 0
    },
    plTotalClass() {
      return this.plTotalNegative ? 'text-danger' : ''
    },
    allocationTotalValue() {
      const classes = this.displayClasses || []
      return allocationTotal(
        classes.map((classe) => this.resolveGroupSumNumber(classe && classe[1]))
      )
    },
    assetPctById() {
      const total = this.allocationTotalValue
      const classes = this.displayClasses || []
      const map = {}
      classes.forEach((classe) => {
        this.resolveGroupAssets(classe && classe[1]).forEach((asset) => {
          if (asset && asset.id != null) {
            map[asset.id] = toPercent(
              allocationPercent(this.resolveAssetAplicacao(asset), total) * 100,
              1
            )
          }
        })
      })
      return map
    },
    shouldShowRentability() {
      return !isOffshoreInvestmentType({ investmentType: this.investmentType })
    },
    columns() {
      return this.columnsConfig
    },
    totalVisibleColumns() {
      return this.columns.filter((column) =>
        this.isColumnRendered(column)
      ).length
    },
    totalRowLeadingColspan() {
      const firstSaldoIndex = this.columns.findIndex(
        (column) => column.group === 'Saldo Bruto'
      )
      const leadingColumns = this.columns.slice(0, firstSaldoIndex)
      const leading = leadingColumns.filter((column) =>
        this.isColumnRendered(column)
      ).length
      return leading > 0 ? leading : 1
    },
    totalRowTrailingColspan() {
      const trailing =
        this.totalVisibleColumns - this.totalRowLeadingColspan - 1
      return Math.max(trailing, 0)
    },
    recomendacaoTooltip() {
      const baseUrl = window.location.origin
      return `Recomendação de compra, venda ou manutenção do ativo, definido pelo seu escritório. Configure em lote em ${baseUrl}/investimentos/configuracoes/asset-allocation/assets`
    },
    comentarioTooltip() {
      const baseUrl = window.location.origin
      return `Comentário sobre o ativo. Configure em lote em ${baseUrl}/investimentos/configuracoes/asset-allocation/assets`
    },
    suitabilityByAssetId() {
      return buildSuitabilityMap({
        carteirasRecomendadas: this.carteirasRecomendadas,
        carteiraTabela: this.carteiraTabela,
      })
    },
    ativosDoCliente() {
      return buildFlatAssets(this.carteiraTabela)
    },
    sugestoesPendentesById() {
      const apurado = this.sugestoesPendentesPorAtivo
      if (!apurado) return {}
      return this.allAssetIdsCache.reduce((mapa, id) => {
        mapa[id] = apurado[String(id)] || 0
        return mapa
      }, {})
    },
  },
  watch: {
    carteiraTabela: {
      immediate: true,
      handler(val) {
        this.recomputePortfolioCaches(val)
        this.resetVisibleAssets()
      },
    },
    estatisticasClasse: {
      immediate: true,
      handler(val) {
        this.recomputeClassStatsCache(val)
        this.recomputeDisplayClasses()
      },
    },
    statisticsAssets: {
      immediate: true,
      handler(val) {
        this.recomputeAssetStatsCache(val)
        this.recomputeDisplayClasses()
      },
    },
    searchQuery() {
      this.resetPaginationForAllGroups()
    },
    defaultPageSize(novoTamanhoPadrao) {
      this.pageSize = novoTamanhoPadrao
    },
    clienteAtual(novoCliente, clienteAnterior) {
      const novoId = (novoCliente || {}).id
      const anteriorId = (clienteAnterior || {}).id
      if (novoId === anteriorId) return
      this.pageSizeByGroup = {}
      this.groupPageMap = {}
    },
  },
  mounted() {
    this.resetVisibleAssets()
  },
  methods: {
    isColumnShown(column) {
      return isColumnVisible({ visibleColumns: this.visibleColumns, column })
    },
    isColumnRendered(column) {
      if (column.key === 'acoes') {
        return shouldShowActions({
          readonly: this.readonly,
          visibleColumns: this.visibleColumns,
          allowedActions: this.allowedActions,
        })
      }
      if (column.group === 'Rentabilidade' && !this.shouldShowRentability) {
        return false
      }
      return this.isColumnShown(column.key)
    },
    buildVisibleClasses() {
      const classes = this.displayClasses || []
      const q = (this.searchQuery || '').trim().toLowerCase()
      if (!q) return classes
      const filtered = []
      for (let i = 0; i < classes.length; i++) {
        this.tryPushFilteredClass({ dest: filtered, pair: classes[i], q })
      }
      return filtered
    },
    tryPushFilteredClass({ dest, pair, q }) {
      const name = pair && pair[0]
      if (!name) return
      const group = (pair && pair[1]) || {}
      const match = this.filterAssetsByQuery({
        list: (group && group.carteira) || [],
        q,
      })
      if (match.length > 0) dest.push([name, { ...group, carteira: match }])
    },
    filterAssetsByQuery({ list, q }) {
      const res = []
      for (let j = 0; j < list.length; j++) {
        const a = list[j] || {}
        const nm = ((a && a.nome_ativo) || '').toLowerCase()
        if (nm.indexOf(q) !== -1) res.push(a)
      }
      return res
    },
    buildGroupNode({ classe }) {
      const name = classe && classe[0]
      if (!name) return null
      const baseAssets = this.getBaseAssetsForClass(classe)
      const st = this.resolvePagination({ classe })
      const assets = st.enabled
        ? baseAssets.slice(st.sliceStart, st.sliceEnd)
        : baseAssets
      const selection = this.makeGroupSelection({ name })
      const pager = this.makeGroupPager(st)
      const controls = this.makeGroupControls({ name, st })
      const meta = this.makeGroupMeta({ classe })
      return { name, classe, assets, pager, controls, selection, meta }
    },
    makeGroupSelection({ name }) {
      const fullAssets = this.getFullClassAssets(name)
      const selected = this.ativosSelecionadosPorClasse[name] || []
      const totalCount = fullAssets.length
      const isChecked = totalCount > 0 && selected.length === totalCount
      const isIndeterminate =
        selected.length > 0 && selected.length < totalCount
      const assetIds = []
      for (let i = 0; i < fullAssets.length; i++) {
        const a = fullAssets[i] || {}
        if (a.id !== undefined) assetIds.push(a.id)
      }
      return { isChecked, isIndeterminate, assets: fullAssets, assetIds }
    },
    makeGroupPager(st) {
      const total = st.total
      const page = st.page
      const pages = st.pages
      const start = st.start
      const end = st.end
      const canPrev = st.canPrev
      const canNext = st.canNext
      const showPager = st.showPager
      const label = start + '-' + end + ' de ' + total
      return {
        total,
        page,
        pages,
        start,
        end,
        canPrev,
        canNext,
        showPager,
        label,
      }
    },
    makeGroupControls({ name, st }) {
      const showControls = st.showControls
      const rpp = this.getEffectivePageSize({ name })
      return { showControls, rpp }
    },
    makeGroupMeta({ classe }) {
      const group = classe && classe[1]
      const sum = this.resolveGroupSumNumber(group)
      const sumFmt = group ? toMoney(sum, 2, this.currency) : ''
      const pctFmt = group
        ? toPercent(allocationPercent(sum, this.allocationTotalValue) * 100, 1)
        : ''
      return { sumFmt, pctFmt, sumNegative: sum < 0 }
    },
    resolveGroupAssets(group) {
      if (!group) return []
      return group.carteira && group.carteira.length
        ? group.carteira
        : group.ativos || []
    },
    resolveGroupSumNumber(group) {
      if (!group) return 0
      if (typeof group.sum === 'number') return group.sum

      return this.resolveGroupAssets(group).reduce(
        (acc, asset) => acc + this.resolveAssetAplicacao(asset),
        0
      )
    },
    resolveAssetAplicacao(asset) {
      const item = asset || {}
      if (typeof item['Aplicação'] === 'number') return item['Aplicação']
      if (typeof item['Aplicacao'] === 'number') return item['Aplicacao']
      return Number(item['Aplicação'] || item['Aplicacao']) || 0
    },
    buildAssetFlagsById() {
      const table = this.carteiraTabela || {}
      const classNames = Object.keys(table)
      const map = {}
      for (let i = 0; i < classNames.length; i++) {
        this.addAssetFlagsFromGroup({ map, group: table[classNames[i]] })
      }
      return map
    },
    addAssetFlagsFromGroup({ map, group }) {
      const assets = (group && group.carteira) || []
      for (let j = 0; j < assets.length; j++) {
        const a = assets[j] || {}
        map[a.id] = this.computeAssetFlags(a)
      }
    },
    computeAssetFlags(a) {
      const type = ((a['Tipo'] || a['tipo'] || '') + '').toLowerCase()
      const canShowRent = !BLACK_LIST_CALCULO_RENTABILIDADE.includes(type)
      const hasMissing =
        !BLACK_LIST_DADOS_FALTANTE.includes(type) && !!a.campos_faltantes
      const showMM = !a.marcacao_na_curva && this.showMarcacaoCurva(a)
      return { canShowRent, hasMissing, showMM }
    },
    buildAssetViewById() {
      const flags = this.assetFlagsById || {}
      const alertMap = this.alertMarcacaoMercadoRF || {}
      const ids = Object.keys(flags)
      const res = {}
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]
        const f = flags[id] || {}
        const hasAlert = !!alertMap[id]
        res[id] = this.computeAssetView({ f, hasAlert })
      }
      return res
    },
    computeAssetView({ f, hasAlert }) {
      const canRent = !!f.canShowRent
      const hasMissing = !!f.hasMissing
      const showNoRent = !canRent
      const showAlert = canRent && hasAlert
      const showMissing = canRent && !hasAlert && hasMissing
      const showRentCols = canRent && !hasAlert && !hasMissing
      return {
        showNoRent,
        showAlert,
        showMissing,
        showRentCols,
        showMM: !!f.showMM,
      }
    },
    selecionarAtivosCarteira(val) {
      this.ativosSelecionadosInside = val ? this.allAssetIds.slice(0) : []
    },
    selecionarAtivosClasse(carteira, ev) {
      this.$emit('selecionarAtivosClasse', carteira, ev)
    },
    openSaveBenchmark(classe) {
      this.$emit('openSaveBenchmark', classe)
    },
    resolveDonoDoAtivo(asset) {
      const id = asset && asset.id_cliente
      if (id === null || id === undefined || id === '') return null
      const nome = asset.nome_cliente || asset.membro || ''
      return { id, name: nome, nome_cliente: nome }
    },
    openDetalhes(asset, aba) {
      let mov = this.movimentacoes[asset.id]
      if (mov) mov = { ...mov, ...mov.data }
      const opts = {
        show_alert_fill_pu: this.alertMarcacaoMercadoRF[asset.id],
        movimentacoes: mov ? [mov] : mov,
        // O dono do ativo vence o cliente do shell: na carteira do grupo a
        // tabela mistura titulares, e o modal grava comentario/provento/split
        // sob o cliente que receber aqui.
        clienteSelecionado: this.resolveDonoDoAtivo(asset) || this.clienteAtual,
        tab: aba,
        sugestoesPorMovimentacao: this.sugestoesPorMovimentacao,
        movimentacoesDasSugestoes: this.movimentacoesDasSugestoes,
      }
      GlobalBus.$emit(MODAL.DETAILS_ASSET, asset, opts)
    },
    openNewAtivo(ativo) {
      this.$emit('openNewAtivo', ativo)
    },
    aporteResgate(ativo, modo) {
      this.$emit('aporteResgate', ativo, modo)
    },
    openPortabilidade(ativo) {
      GlobalBus.$emit(MODAL.PORTABILIDADE, ativo)
    },
    openVincular(origem) {
      this.$emit('openVincular', origem)
    },
    openModalDelete(ativo) {
      this.$emit('openModalDelete', ativo)
    },
    resetVisibleAssets() {
      this.recomputeDisplayClasses()
    },
    resetPaginationForAllGroups() {
      const list = this.displayClasses || []
      for (let i = 0; i < list.length; i++) {
        const name = list[i] && list[i][0]
        if (name) this.$set(this.groupPageMap, name, 1)
      }
    },
    recomputeDisplayClasses() {
      this.displayClasses = this.normalizeClasses()
    },
    recomputePortfolioCaches(val) {
      const total = computeTotalInvested(val)
      this.totalInvested = total

      this.viewByClass = buildViewByClass({
        carteiraTabela: val,
        totalInvested: total,
      })
      this.allAssetIdsCache = buildAllAssetIds(val)
    },
    recomputeClassStatsCache(stats) {
      const built = buildClassStatsFmt(stats)
      this.classVolFmtByName = built.vol
      this.classInicioFmtByName = built.inicio
      this.classRendFmtByName = built.rend
    },
    recomputeAssetStatsCache(stats) {
      this.assetStatsFmtById = buildAssetStatsFmt(stats)
    },
    normalizeClasses() {
      return Array.isArray(this.classes) ? this.classes : []
    },
    getFullClassAssets(className) {
      const table = this.carteiraTabela || {}
      const group = table[className] || {}
      const list = group.carteira || []
      return list.slice(0)
    },
    getBaseAssetsForClass(classe) {
      const group = classe && classe[1]
      const list = (group && group.carteira) || []
      return list
    },
    getGroupPage(name) {
      return this.groupPageMap[name] || 1
    },
    getClassRent(classe, bench_selected, with_bench = true) {
      return CalculosConsolidador.getClassRent({
        estatisticas_classe: this.estatisticasClasse,
        classe,
        bench_selected,
        mapper_benchmarks: this.mapperBenchmarks,
        with_bench,
        benchmarks: this.benchmarks,
      })
    },
    showMarcacaoCurva(ativo) {
      const valid =
        ['Tesouro Direto'].includes(ativo.Tipo) ||
        ['CRA', 'CRI', 'DEB'].includes(ativo.subtipo)
      return valid
    },
    hasGroupPageSizeOverride({ name }) {
      return this.getGroupPageSizeOverride({ name }) !== null
    },
    getGroupPageSizeOverride({ name }) {
      const local = this.pageSizeByGroup[name]
      if (local === 'Todos') return 'Todos'
      const localNum = Number(local)
      return localNum > 0 ? localNum : null
    },
    getEffectivePageSize({ name }) {
      const override = this.getGroupPageSizeOverride({ name })
      if (override !== null) return override

      const global = this.pageSize
      if (global === 'Todos') return 'Todos'
      const globalNum = Number(global)
      if (globalNum > 0) return globalNum

      const first = (this.pageSizeOptions || [])[0]
      if (first === 'Todos') return 'Todos'
      const firstNum = Number(first)
      return firstNum > 0 ? firstNum : this.defaultPageSize
    },
    onChangeGroupPageSize({ name, val }) {
      const size =
        val === 'Todos'
          ? 'Todos'
          : Number(val) > 0
          ? Number(val)
          : this.getEffectivePageSize({ name })
      this.$set(this.pageSizeByGroup, name, size)
      this.$set(this.groupPageMap, name, 1)
      this.$emit('updatePageSize', size)
      this.$emit('update:pageSize', size)
    },
    getAssetsForClass(classe) {
      const list = this.getBaseAssetsForClass(classe)
      const st = this.resolvePagination({ classe })
      return st.enabled ? list.slice(st.sliceStart, st.sliceEnd) : list
    },
    nextGroupPage(classe) {
      const st = this.resolvePagination({ classe })
      if (!st.enabled || !st.canNext) return
      this.gotoGroupPage({ classe, page: st.page + 1 })
    },
    prevGroupPage(classe) {
      const st = this.resolvePagination({ classe })
      if (!st.enabled || !st.canPrev) return
      this.gotoGroupPage({ classe, page: st.page - 1 })
    },
    gotoGroupPage({ classe, page }) {
      const st = this.resolvePagination({ classe })
      if (!st.enabled) return
      const name = classe && classe[0]
      const next = Math.min(Math.max(page, 1), st.pages)
      this.$set(this.groupPageMap, name, next)
    },
    getGroupPageInfo(classe) {
      const st = this.resolvePagination({ classe })
      return {
        total: st.total,
        page: st.page,
        pages: st.pages,
        start: st.start,
        end: st.end,
        canPrev: st.canPrev,
        canNext: st.canNext,
        showPager: st.showPager,
        showControls: st.showControls,
      }
    },
    resolvePagination({ classe }) {
      const name = classe && classe[0]
      const list = this.getBaseAssetsForClass(classe)
      const total = list.length
      const groupSize = this.getEffectivePageSize({ name })
      const selectedAll = groupSize === 'Todos'

      if (!this.shouldEnablePagination({ selectedAll, groupSize })) {
        return this.buildDisabledPagination({ total, name, selectedAll })
      }

      const sizeNum = Number(groupSize)
      const pages = Math.max(1, Math.ceil(total / sizeNum))
      const page = Math.min(Math.max(this.getGroupPage(name), 1), pages)
      const sliceStart = (page - 1) * sizeNum
      const sliceEnd = Math.min(sliceStart + sizeNum, total)

      return this.buildEnabledPagination({
        name,
        total,
        page,
        pages,
        sliceStart,
        sliceEnd,
      })
    },
    shouldEnablePagination({ selectedAll, groupSize }) {
      return !!(
        !selectedAll &&
        Number(groupSize) > 0 &&
        this.isPaginationEnabled
      )
    },
    buildDisabledPagination({ total, name, selectedAll }) {
      const start = total === 0 ? 0 : 1
      return {
        enabled: false,
        total,
        page: 1,
        pages: 1,
        start,
        end: total,
        canPrev: false,
        canNext: false,
        showPager: false,
        showControls: this.hasGroupPageSizeOverride({ name }) || !!selectedAll,
        sliceStart: 0,
        sliceEnd: total,
      }
    },
    buildEnabledPagination({ name, total, page, pages, sliceStart, sliceEnd }) {
      const start = total === 0 ? 0 : sliceStart + 1
      const end = total === 0 ? 0 : sliceEnd
      return {
        enabled: true,
        total,
        page,
        pages,
        start,
        end,
        canPrev: page > 1 && total > 0,
        canNext: page < pages && total > 0,
        showPager: pages > 1,
        showControls: pages > 1 || this.hasGroupPageSizeOverride({ name }),
        sliceStart,
        sliceEnd,
      }
    },
    async changeName({ asset, name } = {}) {
      if (!asset) return
      this.$set(asset, 'loadingEdit', true)
      if (this.$currentUser().IS_CLIENTE) {
        await this.changeNameAsClient({ asset, name })
      } else {
        await this.changeNameAsProfessional({ asset, name })
      }
      this.$set(asset, 'loadingEdit', false)
    },
    async changeNameAsClient({ asset, name } = {}) {
      if (!asset) return
      const newName = name || ''
      const dados = {
        id: asset.id,
        id_ativo_cliente: asset.id,
        id_cliente_app: asset.id_cliente,
        id_conta: asset.id_conta,
        nome_ativo: newName,
        dados_ativo: JSON.stringify({ nome_ativo: newName }),
        ativo_novo: JSON.stringify({ nome_ativo: newName }),
      }
      const r = await con.post(cadastroAtivo.ativos.editar, dados)
      if (r && r.status !== 200) {
        this.$showSnack({ message: r.message, button: true })
      } else {
        this.$set(asset, 'nome_ativo', newName)
        GlobalBus.$emit(RELOAD.ASSETS_ONLY)
      }
    },
    async changeNameAsProfessional({ asset, name } = {}) {
      if (!asset) return
      const newName = (name || '').trim()
      if (newName === '') {
        this.$showSnack({
          text: 'O nome não pode ficar em branco',
          button: true,
        })
        return
      }
      const dados = {
        id_cliente_app: asset.id_cliente,
        id_conta: asset.id_conta,
        nome_instituicao: asset.nome_instituicao,
        nome_ativo: newName,
      }
      const r = await con.put(cadastroAtivo.ativos.edit_name + asset.id, dados)
      if (r && r.status !== 200) {
        this.$showSnack({ text: r.message, button: true })
      } else {
        this.$set(asset, 'nome_ativo', newName)
        GlobalBus.$emit(RELOAD.ASSETS_ONLY)
      }
    },
    handleRowOpenDetalhes({ asset, action } = {}) {
      if (!asset) return
      this.openDetalhes(asset, action)
    },
    handleRowOpenNewAsset({ asset } = {}) {
      if (!asset) return
      this.openNewAtivo(asset)
    },
    handleRowAporteResgate({ asset, action } = {}) {
      if (!asset) return
      this.aporteResgate(asset, action)
    },
    handleRowOpenPortabilidade({ asset } = {}) {
      if (!asset) return
      this.openPortabilidade(asset)
    },
    handleRowOpenVincular({ asset } = {}) {
      if (!asset) return
      this.openVincular(asset)
    },
    handleRowOpenDelete({ asset } = {}) {
      if (!asset) return
      this.openModalDelete(asset)
    },
    handleRowUpdateSelected({ value } = {}) {
      this.ativosSelecionadosInside = value || []
    },
    resolveAssetRendText(asset) {
      const assetId = asset.id
      const statsById = this.statisticsAssets || {}
      const assetStats = statsById[assetId] || {}
      // Terser 4.8.1 colapsa `x == null ? (a && b) : x` em `x ?? a && b` (sem
      // parenteses) — SyntaxError no bundle, barrado pelo gate validate-bundles
      // (incidente 2026-06-19 v4.145.0). Reescrito como if explicito p/ evitar a
      // otimizacao de coalesce do minificador. Comportamento identico.
      let rawValue = assetStats.rendimento
      if (rawValue == null) {
        rawValue = asset.lucro_prejuizo
      }
      const fallbackById = this.assetStatsFmtById || {}
      const fallbackStats = fallbackById[assetId] || {}
      return formatRentabilityByCurrency({
        rawValue,
        currencyKey: 'real',
        fallbackValue: fallbackStats.rendFmt || '',
      })
    },
    resolveAplicacao(asset) {
      const raw = this.pickAplicacaoRaw(asset)
      const text = String(raw || '')
      if (!this.isInvalidMoneyText(text)) {
        return { text, number: this.resolveAssetAplicacao(asset) }
      }
      const fallback = this.resolveAplicacaoFallback(asset)
      return { text: toMoney(fallback, 2, this.currency), number: fallback }
    },
    resolveAplicacaoFallback(asset) {
      const item = asset || {}
      return Number(item['Aplicação'] || item['Aplicacao'] || item.pl || 0) || 0
    },
    pickAplicacaoRaw(asset) {
      if (asset['Aplicação_f'] !== undefined) return asset['Aplicação_f']
      if (asset['Aplicacao_f'] !== undefined) return asset['Aplicacao_f']
      return ''
    },
    isInvalidMoneyText(text) {
      if (!text) return true
      if (text.trim() === '- R$nan') return true
      return text.toLowerCase().includes('nan')
    },
  },
}
</script>

<template>
  <div class="assets-by-class-table">
    <table class="assets-by-class-table__table">
      <AssetsByGroupTableHeader
        :allowed-actions="allowedActions"
        :columns-config="columnsConfig"
        :readonly="readonly"
        :is-all-selected="isAllSelected"
        :is-some-selected="isSomeSelected"
        :show-rentability="shouldShowRentability"
        :loading-rent-asset="loadingRentAsset"
        :recomendacao-tooltip="recomendacaoTooltip"
        :comentario-tooltip="comentarioTooltip"
        :visible-columns="visibleColumns"
        @select-all="selecionarAtivosCarteira"
      >
        <template v-if="$scopedSlots['actions-header']" v-slot:actions-header>
          <slot name="actions-header" />
        </template>
      </AssetsByGroupTableHeader>

      <tbody v-if="groups.length === 0">
        <tr class="assets-by-class-table__no-results">
          <td
            class="assets-by-class-table__no-results-column"
            :colspan="totalVisibleColumns"
          >
            Nenhum ativo encontrado para a sua pesquisa.
          </td>
        </tr>
      </tbody>

      <template v-else>
        <tbody v-for="g in groups" :key="g.name">
          <AssetsByGroupTableGroupRow
            :allowed-actions="allowedActions"
            :columns-config="columnsConfig"
            :group="g"
            :estatisticas-classe="estatisticasClasse"
            :show-rentability="shouldShowRentability"
            :readonly="readonly"
            :map-class-rent="mapClassRent"
            :benchmarks-by-class="benchmarksByClass"
            :class-vol-fmt-by-name="classVolFmtByName"
            :class-inicio-fmt-by-name="classInicioFmtByName"
            :class-rend-text="classRendTextByName[g.name] || ''"
            :visible-columns="visibleColumns"
            @select-classe="selecionarAtivosClasse"
            @open-save-benchmark="openSaveBenchmark"
          />

          <AssetsByGroupTableRow
            v-for="ativo in g.assets"
            :key="g.name + '-' + ativo.id"
            :allowed-actions="allowedActions"
            :columns-config="columnsConfig"
            :asset="ativo"
            :asset-aplicacao-text="assetAplicacaoFmtById[ativo.id]"
            :asset-negative="!!assetNegativeById[ativo.id]"
            :asset-pct-text="assetPctById[ativo.id] || '0,00%'"
            :asset-rend-text="assetRendTextById[ativo.id] || ''"
            :asset-stats="statisticsAssets[ativo.id]"
            :asset-stats-fmt="assetStatsFmtById[ativo.id] || {}"
            :asset-view="assetViewById[ativo.id] || {}"
            :composicao-fundos="composicaoFundos"
            :selected-assets="ativosSelecionadosInside"
            :show-rentability="shouldShowRentability"
            :readonly="readonly"
            :visible-columns="visibleColumns"
            :suitability="suitabilityByAssetId[ativo.id] || null"
            :sugestoes-pendentes="sugestoesPendentesById[ativo.id]"
            :ativos-do-cliente="ativosDoCliente"
            :movimentacoes-das-sugestoes="movimentacoesDasSugestoes"
            @aporte-resgate="handleRowAporteResgate"
            @change-name="changeName"
            @open-delete="handleRowOpenDelete"
            @open-detalhes="handleRowOpenDetalhes"
            @open-new-asset="handleRowOpenNewAsset"
            @open-portability="handleRowOpenPortabilidade"
            @open-vincular="handleRowOpenVincular"
            @update-selected-assets="handleRowUpdateSelected"
          />

          <tr
            v-if="g.controls.showControls"
            :key="g.name + '-pager'"
            class="assets-by-class-table__group-pager"
          >
            <td
              class="assets-by-class-table__group-pager-column"
              :colspan="totalVisibleColumns"
            >
              <div class="assets-by-class-table__group-pager-content">
                <div class="assets-by-class-table__group-pager-rpp">
                  <span>Linhas por página:</span>
                  <v-select
                    :value="g.controls.rpp"
                    :items="pageSizeOptions"
                    :menu-props="{ offsetY: true }"
                    dense
                    hide-details
                    class="assets-by-class-table__group-pager-rpp-select"
                    @change="
                      (val) => onChangeGroupPageSize({ name: g.name, val })
                    "
                  />
                </div>

                <div class="assets-by-class-table__group-pager-text">
                  {{ g.pager.label }}
                </div>

                <div class="assets-by-class-table__group-pager-actions">
                  <v-btn
                    icon
                    :disabled="!g.pager.canPrev"
                    @click="prevGroupPage(g.classe)"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    :disabled="!g.pager.canNext"
                    @click="nextGroupPage(g.classe)"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </template>

      <tbody>
        <tr class="assets-by-class-table__table-total">
          <td
            class="assets-by-class-table__table-total-column assets-by-class-table__table-total-column--ativo"
            :colspan="totalRowLeadingColspan"
          >
            PL Total
          </td>
          <td
            class="assets-by-class-table__table-total-column"
            :class="plTotalClass"
          >
            {{ plTotalText }}
          </td>
          <td
            v-if="totalRowTrailingColspan > 0"
            class="assets-by-class-table__table-total-column"
            :colspan="totalRowTrailingColspan"
          ></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="sass">
$consolidator-border: 1px solid #8E8E8E1A

@mixin table-borders()
  width: 100%
  border-collapse: collapse

  thead tr th
    padding-top: 0px !important

  th, td
    border-right: $consolidator-border

  th:last-child,
  td:last-child
    border-right: none

  td
    text-align: center
    vertical-align: middle

    &:first-child
      text-align: left

  tbody tr:hover
    background-color: var(--gray-3) !important

@mixin flex-center-with-gap($gap: 8px, $min-height: 45px)
  display: flex
  gap: $gap
  align-items: center
  min-height: $min-height

.assets-by-class-table
  display: flex
  flex-direction: column
  gap: 16px
  width: 100%
  overflow-x: auto
  max-height: none
  overflow-y: visible

  &__search
    margin-left: auto

  &__table
    @include table-borders()

    &-header
      &-column
        padding: 12px 10px

        &:first-child
          @include flex-center-with-gap()

      &-text
        font-size: 14px !important
        color: #5B5B5B !important
        font-weight: 600 !important
        width: 100%
        min-height: 45px
        display: flex
        justify-content: center
        align-items: center
        gap: 2px
        background: #8E8E8E33
        padding: 12px !important
        border-radius: 6px

        &--loading
          flex-direction: column
          gap: 4px

    &-classe
      font-size: 12px !important
      color: #5B5B5B !important
      font-weight: 600 !important
      background: #F4F4F480

      &-column
        padding: 8px 10px

        &--checkbox
          @include flex-center-with-gap()
          max-width: 300px

        &--money
          min-width: 128px
          white-space: nowrap

    &-total
      font-size: 12px !important
      font-weight: bold !important

      &-column
        height: 48px
        background: #F4F4F480
        padding: 8px 10px

        &:nth-child(2)
          text-align: center !important
          vertical-align: middle

  &__no-results
    font-size: 14px !important
    color: #5B5B5B !important
    border:
      top: $consolidator-border
      bottom: $consolidator-border

    &-column
      text-align: center !important
      padding: 16px 10px
      background: #F4F4F433

  &__group-pager
    font-size: 12px !important
    border-bottom: $consolidator-border

    &-column
      padding: 8px 10px
      background: #FDFDFD

    &-content
      display: flex
      align-items: center
      justify-content: flex-end
      gap: 32px
      min-height: 40px

    &-rpp
      display: flex
      align-items: center
      gap: 16px

      .v-select
        padding: 0px
        margin: 0px

    &-rpp-select
      max-width: 45px
</style>
